import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { getScheduleContent, getScheduleVersions, getScheduleDemand, getControllersForChangingShift } from '../../../services/monthlyPlan.service';
import { sendShiftChangeRequest, getShiftChangeRequests, sendShiftRequestResponse } from '../../../services/changeShift';
import { useHistory } from "react-router-dom";
 
const useShiftsChange = ({ atcu, year, month, userId, dateForControllers }) => {
  const [loading, setLoading] = useState();
  const [version, setVersion] = useState();
  const [nextMonthVersion, setNextMonthVersion] = useState();
  const [dataSource, setDataSource] = useState();
  const [controllersList, setControllersList] = useState(null);
  const [demand, setDemand] = useState();
  const [requestList, setRequestList] = useState();
  const [error, setError] = useState();
 
  const history = useHistory();
 
  const { t } = useTranslation();
  const getAllRequests = async (userId) => {
    try {
      setLoading(true);
      const res = await getShiftChangeRequests(userId, month);
      let requests = res?.data || [];
 
      // If after 15th, fetch next month's requests
      const today = new Date().getDate();
      if (today > 15) {
        const nextMonth = month === 12 ? 1 : month + 1;
        const nextMonthYear = month === 12 ? year + 1 : year;
       
        const nextMonthRes = await getShiftChangeRequests(userId, nextMonth);
        if (nextMonthRes?.data) {
          // Combine current and next month's requests
          requests = [...requests, ...nextMonthRes.data];
        }
      }
 
      setRequestList(requests);
    } catch (e) {
      console.log(e);
      setLoading(false);
      setError(t('shiftsChange.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };
 
  const sendRequest = async (payload) => {
    setLoading(true);
    try {
      await sendShiftChangeRequest(payload);
      message.success(t('shiftsChange.requestSuccess'));
      await getAllRequests(userId);
      history.push('/changeShift/changeShiftRequests');
    } catch (e) {
      message.error(e?.data);
      setError(e);
      history.push('/changeShift');
      console.log(e);
    } finally {
      setLoading(false);
    }
  };
 
  const respondToRequest = async (requestId, userId, response) => {
    try {
      setLoading(true);
      await sendShiftRequestResponse({
        id: requestId,
        userId,
        replacementShiftResponse: response
      });
      if (response === 'APPROVED') {
        message.success(t('shiftsChange.approveSuccess'));
      } else if (response === 'REJECTED') {
        message.success(t('shiftsChange.rejectSuccess'));
      }
      getAllRequests(userId);
    } catch (e) {
      console.log(e);
      if (response === 'APPROVED') {
        message.error(t('shiftsChange.approveFail'));
      } else if (response === 'REJECTED') {
        message.error(t('shiftsChange.rejectFail'));
      }
    } finally {
      setLoading(false);
    }
  };
 
  const getCurrentMonthScheduleVersions = async () => {
    setLoading(true);
    try {
      const res = await getScheduleVersions(atcu, year, month);
      let latestVersion;
      res?.data?.[0]?.versions?.forEach((version) => {
        if (!latestVersion) {
          latestVersion = {
            version: version?.version,
            id: version?.id
          }
        } else if (version?.version < latestVersion) {
          latestVersion = {
            version: version?.version,
            id: version?.id
          }
        }
      });
      setVersion(latestVersion);
     
     // If after 15th, also fetch next month's version
     const today = new Date().getDate();
     if (today > 15) {
       const nextMonth = month === 12 ? 1 : month + 1;
       const nextMonthYear = month === 12 ? year + 1 : year;
       
       const nextMonthRes = await getScheduleVersions(atcu, nextMonthYear, nextMonth);
       let nextMonthLatestVersion;
       nextMonthRes?.data?.[0]?.versions?.forEach((version) => {
         if (!nextMonthLatestVersion) {
           nextMonthLatestVersion = {
             version: version?.version,
             id: version?.id
           }
         } else if (version?.version < nextMonthLatestVersion) {
           nextMonthLatestVersion = {
             version: version?.version,
             id: version?.id
           }
         }
       });
       setNextMonthVersion(nextMonthLatestVersion);
     }
    } catch (e) {
      console.log(e);
      setLoading(false);
      setError(t('shiftsChange.fetchFailed'));
    }
  };
 
  const getCurrentMonthSchedule = async () => {
    setLoading(true);
    setDataSource(null);
    try {
      const res = await getScheduleContent(atcu, year, month, version?.version);
      let currentMonthData = res?.data;
 
      // If after 15th, fetch next month's data
      const today = new Date().getDate();
      if (today > 15) {
        const nextMonth = month === 12 ? 1 : month + 1;
        const nextMonthYear = month === 12 ? year + 1 : year;
       
        const nextMonthRes = await getScheduleContent(atcu, nextMonthYear, nextMonth, nextMonthVersion?.version);
        if (nextMonthRes?.data) {
          // Store next month's data separately
          currentMonthData = {
            ...currentMonthData,
            nextMonthData: nextMonthRes.data
          };
        }
      }
 
      setDataSource(currentMonthData);
    } catch (e) {
      console.log(e);
      setDataSource(null);
      setLoading(false);
      setError(t('shiftsChange.fetchFailed'));
    }
  };
 
  const getCurrentMonthDemand = async () => {
    setLoading(true);
    setDemand(null);
    try {
      const res = await getScheduleDemand(atcu, year, month, version?.version);
      setDemand(res?.data?.shifts);
    } catch (e) {
      console.log(e);
      setDemand(null);
      setLoading(false);
      setError(t('shiftsChange.fetchFailed'));
    }
  };
 
  const fetchControllersForChangingShift = async(atcu, dateForControllers) => {
    try {
      setLoading(true);
      if (dateForControllers) {
        const res = await getControllersForChangingShift(atcu, dateForControllers);
        setControllersList(res);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      // setError(t('shiftsChange.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    if (version) {
      getCurrentMonthSchedule();
    }
  }, [version, nextMonthVersion]);
 
  useEffect(() => {
    getCurrentMonthScheduleVersions();
    getAllRequests(userId);
    if (dataSource && version) {
      getCurrentMonthDemand();
    }
  }, []);
 
  useEffect(() => {
    fetchControllersForChangingShift(atcu, dateForControllers);
  }, [dateForControllers]);
 
  return {
    loading,
    dataSource,
    version,
    nextMonthVersion,
    getCurrentMonthScheduleVersions,
    getCurrentMonthSchedule,
    demand,
    sendRequest,
    requestList,
    respondToRequest,
    error,
    fetchControllersForChangingShift,
    controllersList
  };
};
 
export default useShiftsChange;